import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismIconBrand,
  PrismTooltip,
  styleEntry,
} from '@mylighthouse/prism-ember';
import { PMS_SETTINGS_OFFSET_TYPES } from 'lumen/utils/constants/pricing-assistant-constants';
import safeMultiply from 'lumen/utils/numeric/safe-multiply';

interface SettingsPmsIntegrationRoomtypeHierarchyTreeNodeSignature {
  Args: {
    channelId: unknown;
    channelRoomNameToColorMapping: unknown;
    localSettingsCopy: unknown;
    name: unknown;
    pmsPrefillOptions: unknown;
  };
}

export default class SettingsPmsIntegrationRoomtypeHierarchyTreeNode extends Component<SettingsPmsIntegrationRoomtypeHierarchyTreeNodeSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  @service
  declare pricingAssistantPmsAccess: Services['pricing-assistant-pms-access'];

  LEADING_ROOM_PRICE = 200;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get bookingRoomColor() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.args.channelRoomNameToColorMapping[this.bookingRoomName];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get bookingRoomName() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.args.localSettingsCopy.pms_id_to_booking_room_name_mapping[
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.args.name
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get bookingRoomPriceWithOffset() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const offsetObject = this.args.localSettingsCopy.pms_root_rate_offset;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const offsetType = offsetObject.type;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    let offsetValue = offsetObject.value;

    if (offsetType === PMS_SETTINGS_OFFSET_TYPES.RELATIVE) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      offsetValue = safeMultiply(offsetValue, 100);
    }

    // In case of absolute offset, we add the offset value to the corresponding pms room price
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    let priceToBeShown = this.pmsRoomPriceWithOffset.value + offsetValue;
    if (offsetType === PMS_SETTINGS_OFFSET_TYPES.RELATIVE) {
      // In case of relative offset, we need to add the offset percentage to the corresponding pms room price
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      priceToBeShown =
        this.pmsRoomPriceWithOffset.value +
        (this.pmsRoomPriceWithOffset.value * offsetValue) / 100;
    }

    return {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      value: priceToBeShown,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      formattedValue: this.hotelAndMarketService.formatRate(priceToBeShown),
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPmsLeadingRoom() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const pmsLeadingRoom = this.args.localSettingsCopy.pms_leading_room_id;

    return pmsLeadingRoom === this.pmsRoomName;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get pmsRoomCode() {
    if (this.args.pmsPrefillOptions) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return this.args.pmsPrefillOptions.pmsPrettyRoomNamesToShortNames[
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.pmsRoomName
      ];
    }

    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get pmsRoomName() {
    return this.args.name;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get pmsRoomPriceWithOffset() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const offsetObject = this.args.node.offsets[0];

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const offsetType = offsetObject.type;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    let offsetValue = offsetObject.value;

    if (offsetType === PMS_SETTINGS_OFFSET_TYPES.RELATIVE) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      offsetValue = safeMultiply(offsetValue, 100);
    }

    // Note: This will need to be updated for multi level hierarchy as then we'll add the offset to the respective parent node's price instead of leading room price
    const parentNodePrice = this.LEADING_ROOM_PRICE;

    // In case of absolute offset, we add the offset value to the parent node's price
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    let priceToBeShown = parentNodePrice + offsetValue;
    if (offsetType === PMS_SETTINGS_OFFSET_TYPES.RELATIVE) {
      // In case of relative offset, we need to add the offset percentage to the parent node's price
      priceToBeShown = parentNodePrice + (parentNodePrice * offsetValue) / 100;
    }

    return {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      value: priceToBeShown,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      formattedValue: this.hotelAndMarketService.formatRate(priceToBeShown),
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showRoomPriceWithOffset() {
    // In case of non-editable hierarchy, we show the dummy room prices for only the leading room and not for other rooms
    return (
      this.pricingAssistantPmsAccess.hasEditableHierarchy ||
      this.isPmsLeadingRoom
    );
  }

  <template>
    <div class="roomtype-hierarchy-node flex flex-row items-center">
      <div
        class="roomtype-hierarchy-node__color-block flex items-center justify-center"
        style={{styleEntry "background-color" this.bookingRoomColor}}
      >
        {{#if this.pmsRoomCode}}
          <span
            class="roomtype-hierarchy-node__color-block__room-code text-200--bold truncate"
          >{{this.pmsRoomCode}}</span>
          <PrismTooltip @side="top" @text={{this.pmsRoomCode}} />
        {{/if}}
      </div>

      <div class="roomtype-hierarchy-node__details">
        <div class="flex flex-row justify-between">
          {{! @glint-expect-error: Incorrect type }}
          <p class="text-300--bold">{{this.pmsRoomName}}</p>
          {{#if this.showRoomPriceWithOffset}}
            <p
              class="text-300--bold roomtype-hierarchy-node__details__price"
            >{{this.pmsRoomPriceWithOffset.formattedValue}}</p>
          {{/if}}
        </div>

        <div class="mt-100 flex flex-row justify-between">
          <div class="flex flex-row items-center">
            <div class="mr-100">
              {{! @glint-expect-error: Incorrect type }}
              <PrismIconBrand @name={{@channelId}} />
            </div>
            {{#if this.isPmsLeadingRoom}}
              <p class="text-200--regular">
                {{this.bookingRoomName}}
              </p>
            {{/if}}
          </div>
          {{#if this.showRoomPriceWithOffset}}
            <p
              class="text-200--regular roomtype-hierarchy-node__details__price"
            >{{this.bookingRoomPriceWithOffset.formattedValue}}</p>
          {{/if}}
        </div>
      </div>
    </div>
  </template>
}
